import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from 'config-hub-server/src/router/index.js';
import { diffGroups, recordGroups, type DiffGroup } from '../../domain/entityDiff.js';
import { ENTITY_TYPES, type EntityType } from '../../domain/entityTypes.js';
import { fieldOrder, fieldSpec } from '../../domain/fields.js';
import { isEntityType } from '../../domain/labels.js';
import { countWord } from '../../domain/pluralise.js';

type Inputs = inferRouterInputs<AppRouter>;
type Outputs = inferRouterOutputs<AppRouter>;
export type PushCells = Inputs['push']['run']['cells'];
export type PushPreview = Outputs['push']['preview']['environments'][number];
export type PushEnvironmentResult = Outputs['push']['run']['environments'][number];
/** What the modal opens with: the records (null = every drift and missing record), and which environments to pre-tick. */
export type PushRequest = { cells: PushCells; environments?: string[] };

const SKIP: Record<string, string> = {
    in_sync: 'already in sync',
    excluded: 'not targeted here',
    env_only: 'only in the environment',
    unknown: 'not comparable',
    'no master record': 'not in master',
};
export const writesOf = (p: Pick<PushPreview, 'creates' | 'updates'>): number => p.creates + p.updates;

export const previewSentence = (p: PushPreview): string => {
    if (p.refused) return p.refused;
    const writes = writesOf(p);
    const head =
        writes === 0
            ? ['nothing to push']
            : [
                  countWord(writes, 'write'),
                  ...(p.updates > 0 ? [countWord(p.updates, 'update')] : []),
                  ...(p.creates > 0 ? [countWord(p.creates, 'create')] : []),
              ];
    const skips = Object.entries(p.skipped)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([reason, n]) => `${n} ${SKIP[reason] ?? reason}`);
    return [...head, ...skips].join(' · ');
};

export const canPush = (selected: readonly PushPreview[]): boolean =>
    selected.length > 0 && selected.every((p) => p.refused === null) && selected.some((p) => writesOf(p) > 0);

export const confirmLabel = (selected: readonly PushPreview[]): string => {
    const writes = selected.reduce((s, p) => s + writesOf(p), 0);
    const where = selected.length === 1 ? selected[0]!.name : `${selected.length} environments`;
    return `Push ${countWord(writes, 'write')} to ${where}`;
};

/** The result screen's title: every environment fully applied, some, or none. */
export const resultTitle = (results: readonly PushEnvironmentResult[]): string => {
    if (results.every((r) => r.status === 'applied')) return 'Pushed';
    if (results.some((r) => r.status === 'applied' || r.status === 'partial')) return 'Partly pushed';
    return 'Not pushed';
};

export const resultSentence = (r: PushEnvironmentResult): string => {
    if (r.status === 'refused') return r.refusedReason ?? 'refused';
    const attempted = r.creates + r.updates;
    const parts = [`${r.ok} of ${attempted} written`];
    if (r.conflict > 0) parts.push(countWord(r.conflict, 'conflict'));
    if (r.failed > 0) parts.push(`${r.failed} failed`);
    if (r.refusedReason && r.status === 'failed') parts.push(r.refusedReason);
    return parts.join(' · ');
};

/** What a push record needs of one operation to name its fields and diff them. */
export type OpShape = {
    op: 'create' | 'update';
    entityType: string;
    patch: Record<string, unknown>;
    /** Null for a create, and for an operation stored before ops carried it. */
    before?: Record<string, unknown> | null;
    /** Set on an update that moves a child from this key to `entityKey`. */
    fromKey?: string | null;
};

/**
 * How a push names one operation: its kind's badge, and for a move (an update that moves a child
 * from `fromKey`) the line naming the parent it leaves — read off the type's own registry entry.
 */
export const opKindOf = (op: OpShape): { badge: 'create' | 'update' | 'move'; movedFrom: string | null } => {
    if (!op.fromKey) return { badge: op.op, movedFrom: null };
    const parent = isEntityType(op.entityType) ? ENTITY_TYPES[op.entityType].parentKeyOf(op.fromKey) : null;
    return { badge: 'move', movedFrom: parent ? `Move from ${parent}` : null };
};

/** An operation's type as the field registry knows it; an unknown type falls back to humanised names. */
const typeOf = (op: OpShape): EntityType => op.entityType as EntityType;

/** The detail line: the changed fields' labels in registry order, or `Whole record` for a create. */
export const opFieldsText = (op: OpShape): string => {
    if (op.op === 'create') return 'Whole record';
    const type = typeOf(op);
    const fields = Object.keys(op.patch).sort((a, b) => fieldOrder(type, a) - fieldOrder(type, b));
    return fields.length ? fields.map((field) => fieldSpec(type, field).label).join(', ') : 'No fields';
};

/**
 * What an expanded push record shows. A create is the whole record, after-only. An update
 * diffs the environment's before values against the patch; without before values (an older
 * operation) it is after-only, every written field shown.
 */
export const opDiff = (op: OpShape): { groups: DiffGroup[]; afterOnly: boolean } => {
    const type = typeOf(op);
    if (op.op === 'create') return { groups: recordGroups(type, op.patch), afterOnly: true };
    if (!op.before) return { groups: recordGroups(type, op.patch), afterOnly: true };
    const before = op.before;
    return {
        groups: diffGroups(
            type,
            Object.keys(op.patch).map((field) => ({ field, before: before[field], after: op.patch[field] })),
        ),
        afterOnly: false,
    };
};
